import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Select } from "../../components/Select";
import { Badge } from "../../components/Badge";
import { EmptyState } from "../../components/EmptyState";
import { Link } from "react-router-dom";
import { api, fileUrl } from "../../api/client";
import { useToast } from "../../components/Toast";
import { ExpenseDialog } from "./ExpenseDialog";
import type {
  Expense,
  ExpenseCategory,
  ExpenseCategoryOption,
  ExpenseList,
  QuotePick,
} from "../../api/types";
import { formatMoney } from "../../lib/money";

// 全公司的雜支清單(第 16 輪 Task 3)。**這是財務的頁面** ——
// 登錄本身人人都做得到,入口在報價單上;這一頁是「統整整理」用的。
//
// 分類的中文字樣由後端給(`GET /api/expense-categories`),前端不自己寫一份。

/** 金額一律走 decimal.js。 */
function money(value: string | number | null | undefined): string {
  // 全系統金額一律 0,000,000、四捨五入到整數(業主 2026-10-15)
  return formatMoney(value);
}

export function ExpensesPage() {
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  // 正在修改的那一筆。**登錄完改得動**(第 24 輪)——
  // 以前打錯一個字只能刪掉重登。
  const [editing, setEditing] = useState<Expense | null>(null);
  const [category, setCategory] = useState<ExpenseCategory | "">("");
  // 依報價單篩選(業主 2026-09-30:「希望可以選到報價單看到該張報價單上的所有雜支」)。
  const [quoteId, setQuoteId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const qc = useQueryClient();

  const categories = useQuery({
    queryKey: ["expense-categories"],
    queryFn: () => api.get<ExpenseCategoryOption[]>("/expense-categories"),
  });

  // 列還沒封存的單(與登錄雜支時的下拉同一份):請款掛的多半是還在跑的案子。
  const quotes = useQuery({
    queryKey: ["expense-quote-picks"],
    queryFn: () => api.get<{ items?: QuotePick[] }>("/quotes", { size: 100 }),
  });

  const list = useQuery({
    queryKey: ["expenses", category, quoteId, from, to],
    queryFn: () =>
      api.get<ExpenseList>("/expenses", {
        ...(category ? { category } : {}),
        ...(quoteId ? { quote_id: quoteId } : {}),
        ...(from ? { date_from: from } : {}),
        ...(to ? { date_to: to } : {}),
      }),
  });

  const rows = list.data?.items ?? [];

  // **刪除是丟進回收桶**(第 24 輪):七天內在「回收桶」那一頁救得回來,
  // 所以這一句確認講的是「放進回收桶」而不是「永遠消失」。
  const remove = useMutation({
    mutationFn: (row: Expense) => api.del(`/expenses/${row.id}`),
    onSuccess: () => {
      toast.push("已丟進回收桶,七天內救得回來", "success");
      void qc.invalidateQueries({ queryKey: ["expenses"] });
    },
    onError: (err: unknown) => {
      toast.push(err instanceof Error ? err.message : "刪不掉", "error");
    },
  });

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="m-0 text-xl font-semibold text-[var(--text)]">雜支</h2>
        <Button
          variant="primary"
          icon={<Plus aria-hidden="true" size={16} />}
          onClick={() => setAdding(true)}
        >
          登錄雜支
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        {/* 可見文字與無障礙名稱要**一模一樣** —— 不一樣的話,
            用語音控制的人念出他看到的那幾個字會點不到這一格。
            `Input` / `Select` 的 `label` 會同時當可見文字與無障礙名稱。 */}
        <Select
          label="分類篩選"
          className="w-32"
          value={category}
          onChange={(e) => setCategory(e.target.value as ExpenseCategory | "")}
          options={[
            { value: "", label: "全部" },
            ...(categories.data ?? []).map((one) => ({
              value: one.value,
              label: one.label,
            })),
          ]}
        />
        <Select
          label="報價單篩選"
          className="w-64"
          value={quoteId}
          onChange={(e) => setQuoteId(e.target.value)}
          options={[
            { value: "", label: "全部" },
            ...(Array.isArray(quotes.data?.items) ? quotes.data!.items : []).map((one) => ({
              value: String(one.id),
              label: `${one.quote_no} ${one.title}`.trim(),
            })),
          ]}
        />
        <Input
          label="花費日起日"
          type="date"
          className="w-40"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
        <Input
          label="花費日迄日"
          type="date"
          className="w-40"
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
      </div>

      {/* 財務要的是合計,不是自己拿計算機加。 */}
      <p className="m-0 text-sm text-[var(--text-2)]">
        合計 <strong className="tabular-nums">{money(list.data?.total_amount ?? 0)}</strong>
        <span className="ml-2 text-[var(--text-3)]">共 {list.data?.total ?? 0} 筆</span>
      </p>

      {rows.length === 0 ? (
        <EmptyState
          title="還沒有登錄任何雜支"
          description="車資、材料、便當都算 —— 報了帳,每個案子的毛利才算得準。"
        />
      ) : (
        <div className="w-full overflow-x-auto rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)]">
          <table aria-label="雜支清單" className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="bg-[var(--surface-2)] text-xs font-medium text-[var(--text-2)]">
                <th scope="col" className="whitespace-nowrap px-3 py-2">花費日</th>
                <th scope="col" className="whitespace-nowrap px-3 py-2">分類</th>
                <th scope="col" className="whitespace-nowrap px-3 py-2">店家</th>
                <th scope="col" className="whitespace-nowrap px-3 py-2">金額</th>
                <th scope="col" className="whitespace-nowrap px-3 py-2">發票號碼</th>
                <th scope="col" className="min-w-[14rem] px-3 py-2">歸屬專案</th>
                <th scope="col" className="whitespace-nowrap px-3 py-2">登錄者</th>
                <th scope="col" className="px-3 py-2">憑證</th>
                <th scope="col" className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-[var(--border)]">
                  <td className="whitespace-nowrap px-3 py-2">{row.spent_on}</td>
                  <td className="whitespace-nowrap px-3 py-2">{row.category_label}</td>
                  <td className="whitespace-nowrap px-3 py-2">{row.vendor}</td>
                  <td className="whitespace-nowrap px-3 py-2 tabular-nums">{money(row.amount)}</td>
                  {/* 收據沒有號碼 —— 那是常態,不是資料缺漏,所以用破折號而不是紅字。 */}
                  <td className="whitespace-nowrap px-3 py-2">{row.invoice_no || "—"}</td>
                  <td className="px-3 py-2">
                    {row.quote_id ? (
                      <span>
                        {/* **點得進去,而且是新分頁**(業主 2026-09-21)。
                            走 `<Link>` 而不是原生的 `<a>`:整個站掛在 `/quote/`
                            底下,`<Link>` 會自己補上那一段,原生的不會 ——
                            少了它打開的是 `{"detail":"Not Found"}`。 */}
                        <Link
                          to={`/quotes/${row.quote_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-[var(--accent)] underline-offset-2 hover:underline"
                        >
                          {row.quote_no}
                        </Link>
                        <span className="ml-1 text-xs text-[var(--text-3)]">{row.quote_title}</span>
                      </span>
                    ) : (
                      // 辦公室的影印紙不屬於任何專案 —— **那不是資料缺漏,是刻意的**,
                      // 所以標成「未歸戶」而不是留白。
                      <Badge tone="neutral">未歸戶</Badge>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">{row.recorder_name}</td>
                  {/* 憑證的電子檔。沒傳是常態(業主:「可有可無」),
                      所以用破折號而不是空白 —— 空白看起來像資料掉了。 */}
                  <td className="px-3 py-2">
                    {row.file_url ? (
                      // **要走 `fileUrl()`。** 後端回的是它那一棵樹的絕對路徑
                      // (`/api/files/42`),原樣放進 href 會打到站台根目錄。
                      <a
                        href={fileUrl(row.file_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[var(--accent)] underline-offset-2 hover:underline"
                      >
                        {row.file_name || "檔案"}
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="whitespace-nowrap px-2 py-2 text-right">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" onClick={() => setEditing(row)}>
                        修改
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        icon={<Trash2 aria-hidden="true" size={15} />}
                        onClick={() => {
                          // **先問一句。** 雖然七天內救得回來,但一排長得很像的
                          // 金額很容易按錯列,而發現按錯的成本比問一句高得多。
                          const ok = window.confirm(
                            `要刪掉「${row.vendor || row.category_label}」這筆 ${money(row.amount)} 嗎?

` +
                              "會放進回收桶七天,期間隨時還原得回來。",
                          );
                          if (ok) remove.mutate(row);
                        }}
                      >
                        刪除
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ExpenseDialog
        key={`page-${adding}`}
        open={adding}
        onClose={() => {
          setAdding(false);
          // 視窗裡可能新增或刪了分類(業主 2026-09-30),篩選的下拉跟著重讀。
          void qc.invalidateQueries({ queryKey: ["expense-categories"] });
        }}
        onDone={() => {
          setAdding(false);
          void qc.invalidateQueries({ queryKey: ["expenses"] });
          void qc.invalidateQueries({ queryKey: ["expense-categories"] });
        }}
      />

      {/* 修改那一筆。**`key` 帶著 id** —— 不帶的話換一筆時對話框裡還是上一筆
          的內容(那幾個 useState 的初始值只讀一次)。 */}
      <ExpenseDialog
        key={`edit-${editing?.id ?? "none"}`}
        open={editing !== null}
        expense={editing ?? undefined}
        onClose={() => {
          setEditing(null);
          void qc.invalidateQueries({ queryKey: ["expense-categories"] });
        }}
        onDone={() => {
          setEditing(null);
          void qc.invalidateQueries({ queryKey: ["expenses"] });
          void qc.invalidateQueries({ queryKey: ["expense-categories"] });
        }}
      />
    </div>
  );
}
